import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import PSEAAssessmentForm from "./PSEAAssessmentForm";
import { PSEA_CORE_STANDARDS } from "./pseaCoreStandards";
import { buildPseaFormData } from "./buildPseaFormData";
import { coreStandardsValidationSchema } from "./pseaValidation";
import { usePseaCreator } from "./usePseaCreator";
import { today } from "./pseaDates";
import { useProfile } from "Components/Hooks/UserHooks";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { getContractor } from "common/services/contractors.service";
import { normalize } from "common/lib/normalize";

const initialCoreStandards = PSEA_CORE_STANDARDS.map((s) => ({
  standardNumber: s.number,
  // null = not yet answered, which scores zero just like "No".
  meetsStandard: null as boolean | null,
  // Set only on the standards offering it, where it scores as "Yes" does.
  notApplicable: false,
  evidence: [] as string[],
  otherSpecify: "",
  comment: "",
  selectedFiles: [] as any[],
}));

const AddPSEAAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  usePageActivity(location.pathname, "Add PSEA Assessment");

  const { userProfile } = useProfile();
  const { create } = usePseaCreator();

  const [loading, setLoading] = useState<boolean>(false);

  // When arriving from a contractor's PSEA tab the contractor is passed as a
  // query param, so it can be pre-selected on the form. It is held in state
  // and fed through initialValues (rather than setFieldValue) because the
  // form reinitialises whenever the signed-in profile resolves, which would
  // otherwise clear it.
  const [searchParams] = useSearchParams();
  const presetContractorId = searchParams.get("contractor");
  const [presetContractor, setPresetContractor] = useState<{
    value: string;
    label: string;
  } | null>(null);

  // Started from a contractor's PSEA tab? Return there rather than dropping
  // the user on the global assessments list.
  const returnPath = presetContractorId
    ? `/contractors/${presetContractorId}`
    : "/assessments/psea-assessments";

  useEffect(() => {
    if (!presetContractorId) return;
    let cancelled = false;

    (async () => {
      try {
        const response: any = await getContractor(presetContractorId, {
          fields: ["contractorName", "acronym"],
        });
        const contractor = normalize(response?.data);
        if (cancelled || !contractor?.id) return;
        setPresetContractor({
          value: contractor.id,
          // Matches the option label built by SelectContractor.
          label: `${contractor.contractorName} ( ${contractor.acronym} )`,
        });
      } catch (e) {
        // Fall back to an empty contractor field; the user can pick manually.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [presetContractorId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      contractor: presetContractor || { value: "", label: "" },
      // Defaults to the signed-in user's agency; only PSEA admins may change it.
      agency: {
        value: userProfile?.agency?.id || "",
        label: userProfile?.agency?.name || "",
      },
      dateOfAssessment: today(),
      enteredByName: [userProfile?.firstName, userProfile?.lastName]
        .filter(Boolean)
        .join(" "),
      enteredByEmail: userProfile?.email || "",
      coreStandards: initialCoreStandards,
    },
    validationSchema: Yup.object({
      contractor: Yup.object().shape({
        value: Yup.number().required("Please select a contractor"),
      }),
      // The overall rating is derived from the core standards, so there is no
      // longer a field for the user to fill in.
      coreStandards: coreStandardsValidationSchema,
    }),
    onSubmit: async (values: any) => {
      if (!create) return;
      const formData = buildPseaFormData(values);
      setLoading(true);
      try {
        await create(formData);
        toast.success("PSEA assessment created successfully");
        validation.resetForm();
        navigate(returnPath);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Error while creating PSEA assessment"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Add PSEA Assessment | CIMS";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Add PSEA Assessment"
          back
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            {
              title: "PSEA Assessments",
              link: "/assessments/psea-assessments",
            },
          ]}
        />
        <PSEAAssessmentForm
          validation={validation}
          loading={loading}
          disabled={!create}
          onCancel={() => navigate(returnPath)}
        />
      </Container>
    </div>
  );
};

export default AddPSEAAssessment;
